import { localeForCurrency } from 'utils/currency'

export const MINIMUM_CHARGE_CENTS = 50
const AMOUNT_PLACEHOLDER = '$1'

// Amount digits with separators (. , space, NBSP, narrow NBSP, apostrophe) only between digit groups
const NUMBER_PATTERN = /\d(?:[\d.,\s\u00A0\u202F']*\d)?/
const SEPARATOR_PATTERN = /[.,\s\u00A0\u202F']/g

// Parses an amount_with_symbol string such as "$1,234.56", "€10,45", "1.234,56 €" or "1 234,56 €".
// The last "." or "," is the decimal mark only when 1-2 digits follow it at the end of the
// number; every other separator is a grouping separator. Known limitation: 3-decimal currencies
// (KWD, BHD...) such as "10.456" are read as 10456.
// decimalMark is null when the string has no decimal part (whole amounts such as "€10"); see
// resolveDecimalMark for the mark used when formatting.
// Fails closed: a non-empty string without a readable, non-negative number yields amount NaN
// (never 0, which callers treat as a free booking).
export const parseAmountFromSymbol = (amountWithSymbol) => {
  if (!amountWithSymbol) {
    return { amount: 0, formatPattern: AMOUNT_PLACEHOLDER, decimalMark: '.', groupSeparator: '' }
  }

  const match = amountWithSymbol.match(NUMBER_PATTERN)
  if (!match || /[-\u2212]/.test(amountWithSymbol.slice(0, match.index))) {
    return { amount: NaN, formatPattern: AMOUNT_PLACEHOLDER, decimalMark: '.', groupSeparator: '' }
  }

  const numeric = match[0]
  const decimalMatch = numeric.match(/[.,](\d{1,2})$/)
  const decimalMark = decimalMatch ? numeric[decimalMatch.index] : null
  const integerPart = decimalMatch ? numeric.slice(0, decimalMatch.index) : numeric
  const groupSeparator = (integerPart.match(/[.,\s\u00A0\u202F']/) || [''])[0]
  const digits = integerPart.replace(SEPARATOR_PATTERN, '')
  const amount = Number(decimalMatch ? `${digits}.${decimalMatch[1]}` : digits)

  // Placeholder marks where the formatted numeric part is substituted back in
  const formatPattern = amountWithSymbol.replace(NUMBER_PATTERN, () => AMOUNT_PLACEHOLDER)

  return { amount, formatPattern, decimalMark, groupSeparator }
}

// Minor-unit exponent of a currency (2 for USD/EUR/GBP/AUD/CAD, 0 for JPY...), taken from Intl so no
// table is kept here. Unknown, missing or malformed codes make Intl throw; fall back to 2.
const DEFAULT_EXPONENT = 2
export const minorUnitExponent = (currency) => {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits
  } catch {
    return DEFAULT_EXPONENT
  }
}

// The decimal mark used to format a parsed amount: the one detected in the string, else the opposite
// of a "." or "," group separator (whole amounts like "€1.500"), else the currency's locale mark,
// else ".".
const resolveDecimalMark = ({ decimalMark, groupSeparator }, currency) => {
  if (decimalMark) return decimalMark
  if (groupSeparator === '.') return ','
  if (groupSeparator === ',') return '.'
  try {
    const locale = localeForCurrency(currency)
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).formatToParts(1.1).find((part) => part.type === 'decimal').value
  } catch {
    return '.'
  }
}

// Formats an integer number of minor units using the separators and symbol layout of a parsed amount
export const formatCents = (cents, parsed, exponent = DEFAULT_EXPONENT, currency) => {
  const { formatPattern, groupSeparator } = parsed
  const decimalMark = resolveDecimalMark(parsed, currency)
  const divisor = 10 ** exponent
  const whole = String(Math.floor(cents / divisor)).replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
  const fraction = exponent > 0 ? `${decimalMark}${String(cents % divisor).padStart(exponent, '0')}` : ''
  return formatPattern.replace(AMOUNT_PLACEHOLDER, () => `${whole}${fraction}`)
}

// The currency symbol written before the amount, keeping its trailing space ("CHF ", "kr. ") so the
// "%{symbol}%{min_charge_amount}" locale template reads "CHF 0.50". "" for suffix formats ("$1 €").
// Anything after the amount (e.g. the "USD" in "$75.99 USD") is not part of the symbol.
export const symbolFromPattern = (formatPattern) => formatPattern.slice(0, formatPattern.indexOf(AMOUNT_PLACEHOLDER))

// Applies a coupon to a payment amount using integer minor-unit math that mirrors the Meetings service.
// The amount comes from payment_method.amount_cents when it is a positive integer; otherwise it is
// parsed from the amount_with_symbol string. The string only supplies the display layout.
// isFree is true only when the original amount is known and the final amount is exactly 0; an
// unreadable (NaN) price is never free.
export const computeFinalPayment = (amountWithSymbol, coupon, { amountCents, currency } = {}) => {
  const parsed = parseAmountFromSymbol(amountWithSymbol)
  const exponent = minorUnitExponent(currency)
  const hasAmountCents = Number.isInteger(amountCents) && amountCents > 0
  const originalAmount = hasAmountCents ? amountCents / 10 ** exponent : parsed.amount
  if (!Number.isFinite(originalAmount)) {
    return {
      originalAmount,
      finalAmount: NaN,
      finalCents: NaN,
      isFree: false,
      isMinimumApplied: false,
      formatted: amountWithSymbol,
      parsed,
      exponent
    }
  }
  const originalCents = hasAmountCents ? amountCents : Math.round(originalAmount * 10 ** exponent)

  // Mirrors Meetings base_scheduling_service.rb#calculate_discount_amount_cents: the discount is
  // rounded, the final amount is not.
  let discountCents
  if (coupon?.fixed_amount_cents) {
    discountCents = coupon.fixed_amount_cents
  } else if (coupon?.percent_off) {
    discountCents = Math.round((originalCents * coupon.percent_off) / 100)
  } else {
    return {
      originalAmount,
      finalAmount: originalAmount,
      finalCents: originalCents,
      isFree: originalCents === 0,
      isMinimumApplied: false,
      formatted: amountWithSymbol,
      parsed,
      exponent
    }
  }

  // Mirrors Meetings scheduling_service.rb: free at <= 0, otherwise at least the flat 50 minimum
  let finalCents = originalCents - discountCents
  let isMinimumApplied = false
  if (finalCents <= 0) {
    finalCents = 0
  } else if (finalCents < MINIMUM_CHARGE_CENTS) {
    finalCents = MINIMUM_CHARGE_CENTS
    isMinimumApplied = true
  }

  return {
    originalAmount,
    finalAmount: finalCents / 10 ** exponent,
    finalCents,
    isFree: finalCents === 0,
    isMinimumApplied,
    formatted: formatCents(finalCents, parsed, exponent, currency),
    parsed,
    exponent
  }
}
